import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useSelector } from "react-redux";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import successPopupImg from "../../../assets/images/png/Group 2 2@2x.png";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import { API_ENDPOINT } from "../../../services/api/resources";
import {
  DOCUMENT_UPLOAD_ACCEPT,
  DOCUMENT_UPLOAD_FORMAT_LABEL,
  isDocumentUploadFile,
} from "../../../utils/documentUploadTypes";
import {
  enterpriseBackButtonSx,
  enterpriseBodyTextSx,
  enterpriseBrowseButtonSx,
  enterpriseDangerButtonSx,
  enterprisePageSx,
  enterprisePrimaryButtonSx,
  enterpriseSectionTitleSx,
  enterpriseSupportingTextSx,
  getEnterpriseUploadZoneSx,
} from "../../../components/common/upload/enterpriseUploadStyles";
import {
  getMatchedCatalogItems,
  normalizePriceCatalogItems,
} from "./priceListCatalogMapping";

const getVendorNameFromHeader = (headerInfo = {}) => {
  const vendorDetails = String(headerInfo?.vendor_details || "").trim();
  if (!vendorDetails) {
    return "-";
  }

  return vendorDetails.split("\n")[0]?.trim() || "-";
};

const calculateExtractedGrandTotal = (items = []) =>
  items.reduce((total, item) => {
    const value = Number(String(item?.total_line_value || "").replace(/,/g, ""));
    return total + (Number.isFinite(value) ? value : 0);
  }, 0);

const normalizeCatalogExtractionResponse = (payload, file) => {
  const root =
    payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)
      ? payload.data
      : payload || {};

  const headerInfo = root?.header_info || {};
  const companyDetails = {
    ...(root?.vendor_details || {}),
    ...(root?.company_details || {}),
  };
  const normalizedItems = normalizePriceCatalogItems(getMatchedCatalogItems(payload));
  const calculatedGrandTotal = calculateExtractedGrandTotal(normalizedItems);

  return {
    ...root,
    header_info: headerInfo,
    company_details: {
      ...companyDetails,
      company_name:
        companyDetails?.company_name ||
        root?.company_name ||
        root?.vendor_details?.company_name ||
        root?.vendor_name ||
        root?.supplier_name ||
        getVendorNameFromHeader(headerInfo),
      gst_number:
        companyDetails?.gst_number ||
        root?.gst_number ||
        headerInfo?.gstin ||
        "-",
    },
    document_id: root?.document_id || root?.document_number || file?.name || "Price Catalog",
    document_number: root?.document_id || root?.document_number || file?.name || "Price Catalog",
    buyer_name: root?.buyer_name || "-",
    vendor_name:
      root?.vendor_name ||
      root?.supplier_name ||
      companyDetails?.company_name ||
      getVendorNameFromHeader(headerInfo),
    supplier_name:
      root?.supplier_name ||
      root?.vendor_name ||
      companyDetails?.company_name ||
      getVendorNameFromHeader(headerInfo),
    created_date: root?.created_date || root?.created_at || new Date().toISOString().split("T")[0],
    matched_count: normalizedItems.length,
    line_items: normalizedItems,
    matched_results: {
      ...(root?.matched_results || {}),
      matched_items: normalizedItems,
      grand_total:
        root?.matched_results?.grand_total ??
        root?.totals?.grand_total ??
        root?.grand_total ??
        calculatedGrandTotal,
    },
    grand_total: root?.grand_total ?? calculatedGrandTotal,
    raw_filter_response: payload,
  };
};

const PriceCatalogUploadPage = ({ onBack, onSave }) => {
  const loginData = useSelector((state) => state.loginData.value);
  const fileInputRef = useRef(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [extractError, setExtractError] = useState("");
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
  const [savedRecord, setSavedRecord] = useState(null);

  const authToken =
    loginData?.auth_token ||
    loginData?.access_token ||
    loginData?.token ||
    loginData?.user?.auth_token ||
    "";

  const organizationToken =
    loginData?.organization_token ||
    loginData?.org_token ||
    loginData?.user?.organization_token ||
    loginData?.user?.org_token ||
    "";

  const handleRcDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!isDocumentUploadFile(file)) return;
    setPdfFile(file);
    setExtractedData(null);
    setShowPdfPreview(false);
    setExtractError("");
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!isDocumentUploadFile(file)) return;
    setPdfFile(file);
    setExtractedData(null);
    setShowPdfPreview(false);
    setExtractError("");
  };

  const handleExtract = async () => {
    if (!pdfFile) return;
    try {
      setLoading(true);
      setExtractError("");

      const formData = new FormData();
      formData.append("file", pdfFile);

      const headers = {
        "Content-Type": "multipart/form-data",
      };

      if (authToken) {
        headers.Authorization = `Token ${authToken}`;
      }

      const response = await axios.post(
        API_ENDPOINT.extract_price_catalog_document,
        formData,
        {
          headers,
        },
      );

      const payload = response?.data || {};

      if (payload?.status === false || payload?.success === false) {
        setExtractError(payload?.message || "Unable to extract price catalog.");
        setExtractedData(null);
        return;
      }

      setExtractedData(
        normalizeCatalogExtractionResponse(payload, pdfFile),
      );
    } catch (error) {
      console.error("Error extracting price catalog:", error);
      setExtractError(
        error?.response?.data?.message ||
          error?.response?.data?.response ||
          "Unable to extract price catalog. Please try again.",
      );
      setExtractedData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!extractedData) return;

    try {
      setLoading(true);
      setExtractError("");

      const formData = new FormData();
      formData.append("organization_token", organizationToken);
      formData.append("file", pdfFile);

      const headers = { "Content-Type": "multipart/form-data" };
      if (authToken) headers.Authorization = `Token ${authToken}`;

      const response = await axios.post(
        API_ENDPOINT.save_price_catalog_document,
        formData,
        { headers },
      );

      const payload = response?.data || {};
      if (payload?.status === false || payload?.success === false) {
        setExtractError(payload?.message || "Unable to save price catalog.");
        return;
      }

      setSavedRecord(payload?.data || payload);
      setSaveSuccessOpen(true);
    } catch (error) {
      setExtractError(
        error?.response?.data?.message ||
          "Unable to save price catalog. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClearFile = () => {
    setPdfFile(null);
    setExtractedData(null);
    setShowPdfPreview(false);
    setExtractError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Box sx={enterprisePageSx}>
      <Box
        sx={{
          mt: 1,
          mb: 2,
          ml: -1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <HeaderTitle titleText="Upload Price Catalog" />
        <Button variant="contained" size="small" sx={enterpriseBackButtonSx} onClick={onBack}>
          Back
        </Button>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "space-between" }}>
        <Box
          sx={{
            flex: "1 1 420px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography sx={enterpriseSectionTitleSx}>
            Price Catalog 
          </Typography>

          <Box
            onDrop={handleRcDrop}
            onDragOver={handleDragOver}
            sx={{ ...getEnterpriseUploadZoneSx(false), maxWidth: "896px" }}
          >
            <Typography sx={{ ...enterpriseBodyTextSx, mb: 0.5, wordBreak: "break-all" }}>
              {pdfFile ? pdfFile.name : "Drag and drop file here"}
            </Typography>

            <Typography sx={enterpriseSupportingTextSx}>
              (Supported: {DOCUMENT_UPLOAD_FORMAT_LABEL})
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="medium"
                sx={enterpriseBrowseButtonSx}
                onClick={() => fileInputRef.current?.click()}
              >
                Browse
              </Button>

              {pdfFile && (
                <>
                  <Button
                    variant="outlined"
                    size="medium"
                    sx={enterpriseDangerButtonSx}
                    onClick={handleClearFile}
                  >
                    Clear
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept={DOCUMENT_UPLOAD_ACCEPT}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {showPdfPreview && pdfFile && (
        <Box
          sx={{
            mt: 2,
            p: 1,
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            backgroundColor: "white",
            maxWidth: "720px",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "#075b97",
              fontWeight: 600,
              mb: 1,
              fontSize: 14,
              textAlign: "left",
            }}
          >
            PDF Preview:
          </Typography>
          <iframe
            src={URL.createObjectURL(pdfFile)}
            title="PDF Preview"
            width="100%"
            height="250px"
            style={{
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mt: 2, mb: 1.5 }}>
        <Button
          variant="contained"
          size="small"
          sx={{ ...enterprisePrimaryButtonSx, opacity: !pdfFile || loading || extractedData ? 0.7 : 1, fontSize: "14px" }}
          onClick={handleExtract}
          disabled={!pdfFile || loading || Boolean(extractedData)}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : "Extract"}
        </Button>

        {extractedData && (
          <Button
            variant="contained"
            size="small"
            sx={{ ...enterprisePrimaryButtonSx, opacity: loading ? 0.7 : 1, fontSize: "14px" }}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? <CircularProgress size={16} color="inherit" /> : "Save"}
          </Button>
        )}
      </Box>

      {extractedData?.line_items?.length > 0 && (
        <Box sx={{ mt: 3, width: "100%", overflowX: "auto" }}>
          <Typography sx={{ ...enterpriseSectionTitleSx, mb: 1 }}>
            Extracted Price Catalog Items ({extractedData.line_items.length})
          </Typography>
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}>
            <Table stickyHeader size="small" sx={{ "& .MuiTableCell-root": { fontSize: "14px" } }}>
              <TableHead>
                <TableRow>
                  {["S.No", "Item Code", "Item Name", "Description / Specification", "Qty", "UOM", "Unit Price", "GST", "Price Incl. GST"].map((header) => (
                    <TableCell key={header} sx={{ fontWeight: 600, backgroundColor: "#e0e9ee", whiteSpace: "nowrap" }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {extractedData.line_items.map((item, index) => (
                  <TableRow key={`${item?.row_number || index}-${item?.item_code || index}`}>
                    <TableCell>{item?.row_number || index + 1}</TableCell>
                    <TableCell>{item?.item_code || item?.part_no || ""}</TableCell>
                    <TableCell>{item?.item_name || ""}</TableCell>
                    <TableCell>{item?.description || item?.extra_fields?.["Specification / Brand"] || ""}</TableCell>
                    <TableCell>{item?.quantity || item?.extra_fields?.MOQ || ""}</TableCell>
                    <TableCell>{item?.uom || ""}</TableCell>
                    <TableCell>{item?.currency || "₹"} {item?.unit_price || item?.extra_fields?.["Unit Price (n)"] || ""}</TableCell>
                    <TableCell>{item?.extra_fields?.GST || item?.gst || ""}</TableCell>
                    <TableCell>{item?.currency || "₹"} {item?.extra_fields?.["Price Incl. GST (n)"] || item?.total_amount_tax_incl || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5, fontSize: "14px", fontWeight: 600 }}>
            Grand Total: ₹ {Number(extractedData?.grand_total || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </Box>
        </Box>
      )}

      {extractError ? (
        <Typography
          sx={{
            color: "#b91c1c",
            fontSize: 14,
            mt: 1,
          }}
        >
          {extractError}
        </Typography>
      ) : null}

      <Modal open={saveSuccessOpen}>
        <Fade in={saveSuccessOpen}>
          <Box>
            <SuccessPopupDashboard
              popupImage={successPopupImg}
              popupHeroText="Price Catalog Saved Successfully!"
              popupDescription="The extracted price catalog has been saved."
              popBtn={true}
              btnText="Okay"
              buyerBtn={true}
              buyerBtnOutline={true}
              btnClick={() => {
                setSaveSuccessOpen(false);
                onSave(savedRecord);
              }}
            />
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default PriceCatalogUploadPage;
